<!-- components/pages/sunset/reports/notestats/components/shared/csv_download_button.vue -->
<template>
  <button
      class="btn btn-outline-secondary btn-sm"
      :disabled="downloading || (needsUser && !userId)"
      @click="downloadCsv"
  >
    <span v-if="downloading">Preparing…</span>
    <span v-else>{{ label }}</span>
  </button>
</template>

<script>
import axios from 'axios';

export default {
  name: 'NoteStatsCsvButton',
  props: {
    mode: {
      type: String,
      required: true, // 'leaderboard'|'totals-global'|'totals-user'|'byday-global'|'byday-user'|'notes-user'|'notes-global'
    },
    // from query_controls slot: { side, start, end, userId, includeOther, windowed, ... }
    params: { type: Object, required: true },
    // from query_controls slot (normalizedQuery)
    query:  { type: Object, default: () => ({}) },
    apiBaseUrl: { type: String, default: '/api/v1/' },
    label: { type: String, default: 'Download CSV' },
    filename: { type: String, default: '' } // optional override
  },
  data() {
    return { downloading: false };
  },
  computed: {
    apiBase() {
      return this.apiBaseUrl.replace(/\/+$/, '');
    },
    userId() {
      return this.params && this.params.userId != null ? String(this.params.userId) : null;
    },
    needsUser() {
      // Only endpoints that require a user id
      return ['totals-user', 'byday-user', 'notes-user'].includes(this.mode);
    },
    endpointPath() {
      switch (this.mode) {
        case 'leaderboard':   return '/sunset/reports/note-stats/leaderboard.csv';
        case 'totals-global': return '/sunset/reports/note-stats/totals/global.csv';
        case 'totals-user':   return `/sunset/reports/note-stats/totals/user/${encodeURIComponent(this.userId)}.csv`;
        case 'byday-global':  return '/sunset/reports/note-stats/totals/global/by-day.csv';
        case 'byday-user':    return `/sunset/reports/note-stats/totals/user/${encodeURIComponent(this.userId)}/by-day.csv`;
        case 'notes-user':    return `/sunset/reports/note-stats/notes/user/${encodeURIComponent(this.userId)}.csv`;
        case 'notes-global':  return '/sunset/reports/note-stats/notes/global.csv';
        default:              return '';
      }
    },
    queryParams() {
      const q = new URLSearchParams();

      // core
      if (this.params?.side)  q.set('side',  String(this.params.side));
      if (this.params?.start) q.set('start', String(this.params.start));
      if (this.params?.end)   q.set('end',   String(this.params.end));

      // filters
      if (this.query?.mask)  q.set('mask',  String(this.query.mask));
      if (this.query?.match) q.set('match', String(this.query.match));
      if (this.query?.not)   q.set('not',   String(this.query.not));
      q.set('includeOther', (this.params?.includeOther ? '1' : '0'));
      if (this.params?.windowed) q.set('windowed', '1');

      // optional per-endpoint extras
      if (this.mode === 'leaderboard' && this.query?.limit) {
        q.set('limit', String(this.query.limit));
      }
      // raw notes CSV typically ignores pagination; omit page/perPage on purpose.

      return q;
    },
    csvUrl() {
      const base = `${this.apiBase}${this.endpointPath}`;
      const qs = this.queryParams.toString();
      return qs ? `${base}?${qs}` : base;
    },
    suggestedFilename() {
      if (this.filename) return this.filename;

      const side = (this.params?.side || 'combined').toLowerCase();
      const d1 = (this.params?.start || '').slice(0, 10).replace(/[^0-9]/g, '');
      const d2 = (this.params?.end || '').slice(0, 10).replace(/[^0-9]/g, '');
      const range = d1 && d2 ? `${d1}-${d2}` : 'range';

      const base = (() => {
        switch (this.mode) {
          case 'leaderboard':   return `notestats_leaderboard_${side}`;
          case 'totals-global': return `notestats_totals_global_${side}`;
          case 'totals-user':   return `notestats_totals_user_${this.userId}_${side}`;
          case 'byday-global':  return `notestats_byday_global_${side}`;
          case 'byday-user':    return `notestats_byday_user_${this.userId}_${side}`;
          case 'notes-user':    return `notestats_notes_user_${this.userId}_${side}`;
          case 'notes-global':  return `notestats_notes_global_${side}`;
          default:              return 'notestats_export';
        }
      })();

      return `${base}_${range}.csv`;
    }
  },
  methods: {
    async downloadCsv() {
      if (!this.endpointPath) return;
      if (this.needsUser && !this.userId) return;

      this.downloading = true;
      try {
        const { data } = await axios.get(this.csvUrl, { responseType: 'blob' });
        const blob = new Blob([data], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = this.suggestedFilename;
        document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(url);
        a.remove();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('CSV download failed', e);
      } finally {
        this.downloading = false;
      }
    }
  }
};
</script>
